/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  BuilderFormSchemaFactory,
  FormRootSchema,
  FormStateSchema,
  PbHousePlanFinderListItem,
} from '@gh/builder-shared';
import {
  collectVisibleValues,
  FormState,
  MaskedTypes,
  toAppFormControl,
  toAppFormGroup,
  whenUsOrAuRegion,
  whenUsRegion,
} from '@gh/common-shared';
import { AutocompleteItem } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  FormMode,
  getControl,
  getControlValue,
  normalizeXeroTrackingFormControls,
  setControlValue,
  latitudeValidator,
  longitudeValidator,
} from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { get } from 'lodash';
import { JobCustomer, JobQuickDetails } from '../../resources/job.model';

@Injectable()
export class JobQuickDetailsFormState extends FormState<JobQuickDetails> {
  constructor(schemaFactory: BuilderFormSchemaFactory,
              private translations: TranslationService) {
    super(schemaFactory);
  }

  get priceBook(): Maybe<AutocompleteItem> {
    return getControlValue(this.group, 'priceBook');
  }

  get housePlan(): Maybe<PbHousePlanFinderListItem> {
    return getControlValue(this.group, 'housePlan');
  }

  get contact(): Maybe<JobCustomer> {
    return getControlValue(this.group, 'contact');
  }

  get isQqShow(): boolean {
    return getControlValue(this.group, 'isQqShow');
  }

  get submitValue(): JobQuickDetails {
    normalizeXeroTrackingFormControls(this.group);
    return {
      ...collectVisibleValues(toAppFormGroup(this.group)),
      salesFolderName: this.translations.get('lbl_quick_job'),
      quotationDescription: this.translations.get('lbl_quick_job'),
    };
  }

  build(): void {
    this.initSchema(<any>{
      isQqShow: false,
    });
  }

  setHousePlan(housePlan: PbHousePlanFinderListItem): void {
    setControlValue(this.group, 'housePlan', housePlan);
    setControlValue(this.group, 'housePlanName', housePlan.name);
  }

  clearHousePlan(): void {
    setControlValue(this.group, 'housePlan', void 0);
    setControlValue(this.group, 'housePlanName', void 0);
  }

  setContact(contact?: JobCustomer): void {
    const assignedContact = this.contact;
    setControlValue(this.group, 'contact', contact);

    if (get(assignedContact, 'contactId') !== get(contact, 'contactId')) {
      this.updateContact(contact);
    }
  }

  updateContact(contact?: JobCustomer): void {
    const hasSalesPerson = isNotNil(contact && contact.salespersonId);
    setControlValue(this.group, 'salesperson', hasSalesPerson ? {
      userId: contact!.salespersonId,
      userName: contact!.salespersonName,
    } : void 0);
    setControlValue(this.group, 'owner1', contact && contact.name);

    toAppFormControl(getControl(this.group, 'salesperson')).updateSchema({
      enabled: isNotNil(contact) && !hasSalesPerson,
    });
  }

  updatePriceBook(pb?: AutocompleteItem): void {
    setControlValue(this.group, 'housePlan', void 0);
    toAppFormControl(getControl(this.group, 'housePlan')).updateSchema({
      enabled: isNotNil(pb),
    });
  }

  protected buildSchema(_: JobQuickDetails, { root }: FormStateSchema): FormRootSchema {
    return root({
      mode: FormMode.CreateMode,
      children: {
        contact: { required: true },
        salesperson: { required: true, enabled: false },
        owner1: { required: true },
        owner2: void 0,
        owner3: void 0,
        siteName: { required: true },
        lotNumber: void 0,
        suburb: {required: true},
        streetNumber: void 0,
        street: {required: true},
        postCode: { visible: whenUsOrAuRegion(), type: MaskedTypes.Zip },
        latitude: { required: true, validators: [latitudeValidator()] },
        longitude: { required: true, validators: [longitudeValidator()] },
        reference: { visible: whenUsRegion() },
        priceBook: { required: true },
        isQqShow: void 0,
        housePlan: { required: true, enabled: false },
        housePlanName: void 0,
        baseDesign: { required: true },
        jobMemo: void 0,
        unitsCount: {
          required: true
        }
      }
    });
  }
}
